import React, { useState, useRef, useEffect } from "react";

interface TooltipProps {
  text: string; // Tooltip text
  children: React.ReactNode; // The element that triggers the tooltip
  defaultText?: string; // Optional default text that can be overridden by text prop
  className?: string; // Additional custom styles
  useLightStyle?: boolean; // Add prop to toggle between styles, default to false (solid style)
}
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const Tooltip: React.FC<TooltipProps> = ({ text, defaultText, children, className = "", useLightStyle = false }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<TooltipPosition>("top");
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use text prop if provided, otherwise fall back to defaultText
  const displayText = text || defaultText || "";

  // Calculate position and handle overflow
  const calculatePosition = () => {
    const tooltipEl = tooltipRef.current;
    const containerEl = containerRef.current;
    if (!tooltipEl || !containerEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    const spaceAbove = containerRect.top;
    const spaceBelow = window.innerHeight - containerRect.bottom;
    const spaceLeft = containerRect.left;
    const spaceRight = window.innerWidth - containerRect.right;

    let newPosition: TooltipPosition = "top"; // Default position

    // Reposition based on available space
    if (spaceAbove < tooltipRect.height && spaceBelow > tooltipRect.height) {
      newPosition = "bottom";
    } else if (spaceBelow < tooltipRect.height && spaceAbove > tooltipRect.height) {
      newPosition = "top";
    } else if (spaceRight < tooltipRect.width && spaceLeft > tooltipRect.width) {
      newPosition = "left";
    } else if (spaceLeft < tooltipRect.width && spaceRight > tooltipRect.width) {
      newPosition = "right";
    }

    setPosition(newPosition);
  };

  // Recalculate position when tooltip is visible
  useEffect(() => {
    if (isVisible) calculatePosition();
  }, [isVisible]);

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowPositionClasses = {
    top: "left-1/2 transform -translate-x-1/2 top-full",
    bottom: "left-1/2 transform -translate-x-1/2 bottom-full",
    left: "top-1/2 transform -translate-y-1/2 left-full",
    right: "top-1/2 transform -translate-y-1/2 right-full",
  };

  // Define colors based on the prop
  const bgColor = useLightStyle ? "bg-primary-100" : "bg-gray-800";
  const textColor = useLightStyle ? "text-gray-900" : "text-white";
  const arrowBorderColor = useLightStyle ? "border-t-primary-100" : "border-t-gray-800"; // Top
  const arrowBorderColorBottom = useLightStyle ? "border-b-primary-100" : "border-b-gray-800"; // Bottom
  const arrowBorderColorLeft = useLightStyle ? "border-l-primary-100" : "border-l-gray-800"; // Left
  const arrowBorderColorRight = useLightStyle ? "border-r-primary-100" : "border-r-gray-800"; // Right

  const arrowStyles = {
    top: `border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 ${arrowBorderColor}`,
    bottom:
      `border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 ${arrowBorderColorBottom}`,
    left: `border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 ${arrowBorderColorLeft}`,
    right:
      `border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 ${arrowBorderColorRight}`,
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      {/* Trigger Element */}
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-pointer"
      >
        {children}
      </div>

      {/* Tooltip */}
      {isVisible && (
        <div
          className={`absolute z-50 ${positionClasses[position]} ${className}`}
          ref={tooltipRef}
          style={{ overflow: "visible" }}
        >
          {/* Tooltip content */}
          <div className={`w-[280px] px-4 py-2 text-sm ${textColor} ${bgColor} rounded-lg shadow-lg whitespace-pre-wrap`}>
            {displayText}
          </div>
          {/* Triangle Pointer */}
          <div className={`absolute ${arrowPositionClasses[position]}`}>
            <div className={`w-0 h-0 ${arrowStyles[position]}`}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;